import { useEffect, useRef } from 'react';

interface SuccessFactorsData {
  productAttributes: Array<{ content: string; impact: number }>;
  serviceCharacteristics: Array<{ content: string; impact: number }>;
  resources: Array<{ content: string; impact: number }>;
  competitiveCapabilities: Array<{ content: string; impact: number }>;
  shortcomings: Array<{ content: string; impact: number }>;
}

interface SuccessFactorsRadarChartProps {
  data: SuccessFactorsData;
  className?: string;
}

export default function SuccessFactorsRadarChart({
  data,
  className = '',
}: SuccessFactorsRadarChartProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set up canvas dimensions - larger for better label readability
    const size = 550;
    canvas.width = size;
    canvas.height = size;

    const centerX = size / 2;
    const centerY = size / 2;
    const radius = size / 2 - 140; // Much more padding for labels

    // Clear canvas
    ctx.clearRect(0, 0, size, size);

    // Calculate total impact scores (count × impact level)
    const calculateTotalScore = (items: Array<{ content: string; impact: number }>) => {
      return items.reduce((total, item) => total + item.impact, 0);
    };

    const categoryScores = {
      productAttributes: calculateTotalScore(data.productAttributes),
      serviceCharacteristics: calculateTotalScore(data.serviceCharacteristics),
      resources: calculateTotalScore(data.resources),
      competitiveCapabilities: calculateTotalScore(data.competitiveCapabilities),
      shortcomings: calculateTotalScore(data.shortcomings),
    };

    // Find the maximum score to scale the chart properly
    const maxScore = Math.max(...Object.values(categoryScores), 1);
    const maxRadius = Math.max(maxScore, 10); // Minimum scale of 10

    // Categories with their angles (pentagon shape)
    const categories = [
      {
        name: 'Product\nAttributes',
        value: categoryScores.productAttributes,
        angle: -Math.PI / 2,
        color: '#3b82f6',
      },
      {
        name: 'Service\nCharacteristics',
        value: categoryScores.serviceCharacteristics,
        angle: -Math.PI / 2 + (2 * Math.PI) / 5,
        color: '#22c55e',
      },
      {
        name: 'Resources',
        value: categoryScores.resources,
        angle: -Math.PI / 2 + (2 * (2 * Math.PI)) / 5,
        color: '#8b5cf6',
      },
      {
        name: 'Competitive\nCapabilities',
        value: categoryScores.competitiveCapabilities,
        angle: -Math.PI / 2 + (3 * (2 * Math.PI)) / 5,
        color: '#f59e0b',
      },
      {
        name: 'Critical\nShortcomings',
        value: categoryScores.shortcomings,
        angle: -Math.PI / 2 + (4 * (2 * Math.PI)) / 5,
        color: '#ef4444',
      },
    ];

    // Draw grid circles with dynamic scaling
    ctx.strokeStyle = '#e5e7eb';
    ctx.lineWidth = 1;
    const gridLevels = 5;
    for (let i = 1; i <= gridLevels; i++) {
      ctx.beginPath();
      ctx.arc(centerX, centerY, (radius * i) / gridLevels, 0, 2 * Math.PI);
      ctx.stroke();
    }

    // Draw grid lines from center to each category
    ctx.strokeStyle = '#e5e7eb';
    ctx.lineWidth = 1;
    categories.forEach((category) => {
      ctx.beginPath();
      ctx.moveTo(centerX, centerY);
      ctx.lineTo(
        centerX + radius * Math.cos(category.angle),
        centerY + radius * Math.sin(category.angle)
      );
      ctx.stroke();
    });

    // Draw category labels with better positioning and readability
    ctx.font = 'bold 13px sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    categories.forEach((category) => {
      const labelDistance = radius + 70;
      const x = centerX + labelDistance * Math.cos(category.angle);
      const y = centerY + labelDistance * Math.sin(category.angle);

      // Handle multi-line labels
      const lines = category.name.split('\n');
      const lineHeight = 16;
      const totalHeight = lines.length * lineHeight;

      // Draw background for better readability
      ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
      const maxLineWidth = Math.max(...lines.map((line) => ctx.measureText(line).width));
      ctx.fillRect(
        x - maxLineWidth / 2 - 10,
        y - totalHeight / 2 - 8,
        maxLineWidth + 20,
        totalHeight + 16
      );

      // Draw border around text background
      ctx.strokeStyle = category.color;
      ctx.lineWidth = 2;
      ctx.strokeRect(
        x - maxLineWidth / 2 - 10,
        y - totalHeight / 2 - 8,
        maxLineWidth + 20,
        totalHeight + 16
      );

      // Draw the text lines
      ctx.fillStyle = category.color;
      lines.forEach((line, index) => {
        const lineY = y + (index - lines.length / 2 + 0.5) * lineHeight;
        ctx.fillText(line, x, lineY);
      });

      // Draw the score below the label
      ctx.font = 'bold 11px sans-serif';
      ctx.fillStyle = '#374151';
      ctx.fillText(`Score: ${category.value}`, x, y + totalHeight / 2 + 20);
      ctx.font = 'bold 13px sans-serif'; // Reset font
    });

    // Draw scale indicators at different positions to avoid overlap
    ctx.fillStyle = '#6b7280';
    ctx.font = '10px sans-serif';
    ctx.textAlign = 'left';
    for (let i = 1; i <= gridLevels; i++) {
      const labelRadius = (radius * i) / gridLevels;
      const scaleValue = Math.round((maxRadius * i) / gridLevels);
      // Position scale labels at 36-degree angle (between pentagon points)
      const scaleAngle = Math.PI / 5;
      const x = centerX + labelRadius * Math.cos(scaleAngle) + 5;
      const y = centerY + labelRadius * Math.sin(scaleAngle);

      // Background for scale labels
      ctx.fillStyle = 'rgba(255, 255, 255, 0.8)';
      const scaleText = scaleValue.toString();
      const scaleWidth = ctx.measureText(scaleText).width;
      ctx.fillRect(x - 2, y - 8, scaleWidth + 4, 16);

      ctx.fillStyle = '#6b7280';
      ctx.fillText(scaleText, x, y);
    }

    // Draw Success Factors data polygon
    if (categories.some((cat) => cat.value > 0)) {
      ctx.beginPath();
      categories.forEach((category, index) => {
        const distance = (category.value / maxRadius) * radius;
        const x = centerX + distance * Math.cos(category.angle);
        const y = centerY + distance * Math.sin(category.angle);

        if (index === 0) {
          ctx.moveTo(x, y);
        } else {
          ctx.lineTo(x, y);
        }
      });
      ctx.closePath();

      // Fill polygon with gradient
      const gradient = ctx.createRadialGradient(centerX, centerY, 0, centerX, centerY, radius);
      gradient.addColorStop(0, 'rgba(139, 92, 246, 0.3)');
      gradient.addColorStop(1, 'rgba(139, 92, 246, 0.1)');
      ctx.fillStyle = gradient;
      ctx.fill();

      // Stroke polygon
      ctx.strokeStyle = '#8b5cf6';
      ctx.lineWidth = 3;
      ctx.stroke();

      // Draw data points with labels
      categories.forEach((category) => {
        const distance = (category.value / maxRadius) * radius;
        const x = centerX + distance * Math.cos(category.angle);
        const y = centerY + distance * Math.sin(category.angle);

        // Draw point
        ctx.beginPath();
        ctx.arc(x, y, 6, 0, 2 * Math.PI);
        ctx.fillStyle = category.color;
        ctx.fill();
        ctx.strokeStyle = '#fff';
        ctx.lineWidth = 3;
        ctx.stroke();

        // Draw value label near the point
        if (category.value > 0) {
          ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
          ctx.font = 'bold 11px sans-serif';
          ctx.textAlign = 'center';
          const valueText = category.value.toString();
          const valueWidth = ctx.measureText(valueText).width;

          // Position value label slightly inward from the point
          const labelOffset = 18;
          const labelX = x - labelOffset * Math.cos(category.angle);
          const labelY = y - labelOffset * Math.sin(category.angle);

          ctx.fillRect(labelX - valueWidth / 2 - 4, labelY - 8, valueWidth + 8, 16);
          ctx.fillStyle = category.color;
          ctx.fillText(valueText, labelX, labelY);
        }
      });
    }
  }, [data]);

  const hasData = Object.values(data).some((category) => category.length > 0);

  if (!hasData) {
    return (
      <div className={`bg-gray-50 rounded-lg p-6 text-center ${className}`}>
        <p className="text-gray-500">No Success Factors data available for radar chart</p>
      </div>
    );
  }

  // Calculate totals for display
  const totals = {
    productAttributes: data.productAttributes.reduce((total, item) => total + item.impact, 0),
    serviceCharacteristics: data.serviceCharacteristics.reduce(
      (total, item) => total + item.impact,
      0
    ),
    resources: data.resources.reduce((total, item) => total + item.impact, 0),
    competitiveCapabilities: data.competitiveCapabilities.reduce(
      (total, item) => total + item.impact,
      0
    ),
    shortcomings: data.shortcomings.reduce((total, item) => total + item.impact, 0),
  };

  return (
    <div className={`bg-white rounded-lg p-4 ${className}`} data-chart-type="success-factors">
      <h4 className="text-lg font-semibold text-gray-900 mb-2 text-center">
        Success Factors Impact Analysis
      </h4>
      <div className="flex justify-center mb-4">
        <canvas ref={canvasRef} className="max-w-full" />
      </div>

      <div className="grid grid-cols-1 gap-2 text-sm">
        <div className="flex items-center justify-between">
          <span className="flex items-center">
            <div className="w-3 h-3 bg-blue-500 rounded-full mr-2"></div>
            Product Attributes
          </span>
          <span className="font-semibold text-blue-700">{totals.productAttributes}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="flex items-center">
            <div className="w-3 h-3 bg-green-500 rounded-full mr-2"></div>
            Service Characteristics
          </span>
          <span className="font-semibold text-green-700">{totals.serviceCharacteristics}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="flex items-center">
            <div className="w-3 h-3 bg-purple-500 rounded-full mr-2"></div>
            Resources
          </span>
          <span className="font-semibold text-purple-700">{totals.resources}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="flex items-center">
            <div className="w-3 h-3 bg-yellow-500 rounded-full mr-2"></div>
            Competitive Capabilities
          </span>
          <span className="font-semibold text-yellow-700">{totals.competitiveCapabilities}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="flex items-center">
            <div className="w-3 h-3 bg-red-500 rounded-full mr-2"></div>
            Critical Shortcomings
          </span>
          <span className="font-semibold text-red-700">{totals.shortcomings}</span>
        </div>
      </div>

      <div className="mt-4 text-xs text-center text-gray-500">
        <p>Total Impact Score = Count of items × Impact Level (Low=1, Medium=2, High=3)</p>
      </div>
    </div>
  );
}
